import { AssistantMessage, Message as MessageType, Part as PartType } from "@opencode-ai/sdk/v2/client"
import { refusedKeySuffix } from "./session-turn-key-refused"
import { modelNotFound } from "./session-turn-model-not-found"
import { brokeredBalanceRefused } from "./session-turn-brokered-balance"
import { providerInitCopy } from "./session-turn-provider-init"
import { dailyBudgetError, formatClock } from "./session-turn-daily-budget"
import { closedByApp, finished } from "./session-turn-interrupted"
import type { SessionStatus } from "@opencode-ai/sdk/v2"
import { useData } from "../context"

import { Binary } from "@opencode-ai/core/util/binary"
import { createEffect, createMemo, createSignal, For, on, onCleanup, ParentProps, Show } from "solid-js"
import {
  AssistantParts,
  isMemoryChipPart,
  Message,
  MessageDivider,
  renderable as isAssistantPartRenderable,
  type UserActions,
} from "./message-part"
import { Button } from "./button"
import { MathCurveLoader } from "./math-curve-loader"
import { Card } from "./card"
import { Icon } from "./icon"
import { SessionRetry } from "./session-retry"
import { currentChecks } from "./session-turn-completion"
import { createAutoScroll, createHeldValue } from "../hooks"
import { useI18n } from "../context/i18n"
import { formatDurationLabel } from "./elapsed"
import {
  ACTIVITY_HOLD_MS,
  holdsActivity,
  QUIET_NOTICE_CALM_MS,
  QUIET_NOTICE_LIVE_MS,
  turnProgress,
  withConnectionPhase,
  type TurnProgress,
} from "./session-turn-progress"
import { describeToolActivity } from "./tool-activity"

function isThinkingLayerPart(part: PartType) {
  return part.type === "reasoning" || part.type === "tool"
}

function isThinkingTimelineBoundary(part: PartType, showReasoningSummaries: boolean) {
  if (part.type === "tool") return true
  return isAssistantPartRenderable(part, showReasoningSummaries) && part.type === "reasoning"
}

function ThinkingStatus(props: {
  working: boolean
  createdAt?: number
  durationMs?: number
  progress: TurnProgress
  // The attempt a reconnect is on, for the " · попытка N" tag.
  connectionAttempt?: number
  // The plan dock above the composer is showing the same activity line; the
  // two sit a few pixels apart, so the status row keeps to the phase then.
  activityInDock?: boolean
  // Settings → «Показывать, чем занята модель»; on unless turned off.
  showActivity?: boolean
}) {
  const i18n = useI18n()
  const [now, setNow] = createSignal(Date.now())
  const [observedAt, setObservedAt] = createSignal(Date.now())
  createEffect(
    on(
      () => [props.working, props.progress.revision],
      () => setObservedAt(Date.now()),
    ),
  )
  createEffect(
    on(
      () => props.working,
      (working) => {
        if (!working) return
        setNow(Date.now())
        const timer = setInterval(() => {
          if (typeof document !== "undefined" && document.hidden) return
          setNow(Date.now())
        }, 1000)
        onCleanup(() => clearInterval(timer))
      },
    ),
  )
  const numfmt = createMemo(() => new Intl.NumberFormat(i18n.locale()))
  const elapsedSecondsFormatter = createMemo(
    () => new Intl.NumberFormat(i18n.locale(), { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
  )
  const elapsedMs = createMemo(() => {
    if (props.working && typeof props.createdAt === "number") return Math.max(0, now() - props.createdAt)
    if (!props.working && typeof props.durationMs === "number") return Math.max(0, props.durationMs)
    return undefined
  })
  const elapsed = createMemo(() => {
    const total = elapsedMs()
    if (typeof total !== "number") return ""
    const t = (key: string, params?: Record<string, string | number | boolean>) => i18n.t(key, params)
    // The live timer ticks once a second, so a tenths digit would sit frozen
    // ("12.3" → "13.3"). Whole seconds while running; the final duration keeps
    // its decimal.
    if (props.working) return formatDurationLabel(Math.floor(total / 1000) * 1000, t, numfmt(), numfmt())
    return formatDurationLabel(total, t, numfmt(), elapsedSecondsFormatter())
  })
  // What the action is, when the model is writing or running one: "собираю
  // презентацию · слайд 6 · 14 КБ" rather than a bare "Готовлю действие".
  const current = createMemo(() => {
    const activity = props.progress.activity
    if (!activity) return undefined
    return describeToolActivity(activity, (key, params) => i18n.t(key, params), i18n.locale())
  })
  // Held across the gap between two tool calls, or the row flips to the phase
  // and back on every call. A closing gate — the turn over, the dock taking
  // the line, the setting off, the answer begun — clears it at once.
  const detail = createHeldValue(
    current,
    ACTIVITY_HOLD_MS,
    () =>
      props.working && !props.activityInDock && props.showActivity !== false && holdsActivity(props.progress.phase),
  )
  const quietNotice = () => (props.showActivity === false ? QUIET_NOTICE_CALM_MS : QUIET_NOTICE_LIVE_MS)
  // The detail replaces the phase rather than trailing it: "Пишу deck.js ·
  // 14 КБ" says more than "Готовлю действие: пишу deck.js" in fewer words.
  const label = () => {
    if (!props.working) return i18n.t("ui.sessionTurn.status.completed")
    const value = detail()
    if (value) return value.charAt(0).toLocaleUpperCase(i18n.locale()) + value.slice(1)
    const phase = i18n.t(`ui.sessionTurn.phase.${props.progress.phase}`)
    const attempt = props.connectionAttempt
    if (props.progress.phase === "reconnecting" && attempt && attempt > 1)
      return `${phase} · ${i18n.t("ui.sessionTurn.connection.attempt", { attempt })}`
    return phase
  }
  const text = createMemo(() => {
    if (!elapsed()) return label()
    return `${label()} • ${elapsed()}`
  })

  return (
    <span data-slot="session-turn-thinking-label" class="inline-flex items-center gap-1.5" aria-label={text()}>
      <Show when={props.working}>
        <MathCurveLoader color="currentColor" sizePx={14} />
      </Show>
      {text()}
      <Show when={props.working && now() - observedAt() >= quietNotice()}>
        <span class="text-text-weak" title={i18n.t("ui.sessionTurn.phase.quietHelp")}>
          · {i18n.t("ui.sessionTurn.phase.quiet", { seconds: Math.floor((now() - observedAt()) / 1000) })}
        </span>
      </Show>
    </span>
  )
}

// Under the status row while the request has not reached the server for a
// while, or after a network retry: the one hint that is actionable from the
// user's side. Worded so it does not blame them — the server itself may be
// the slow party — and the last failure's text rides in the tooltip.
const CONNECTION_HINT_AFTER_MS = 10_000
function ConnectionHint(props: { since?: number; retried: boolean; reason?: string }) {
  const i18n = useI18n()
  const [now, setNow] = createSignal(Date.now())
  const timer = setInterval(() => setNow(Date.now()), 1000)
  onCleanup(() => clearInterval(timer))
  const visible = () =>
    props.retried || (typeof props.since === "number" && now() - props.since >= CONNECTION_HINT_AFTER_MS)
  return (
    <Show when={visible()}>
      <div data-slot="session-turn-connection-hint" class="text-12-regular text-text-weak" title={props.reason}>
        {i18n.t("ui.sessionTurn.connection.slow")}
      </div>
    </Show>
  )
}

function record(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === "object" && !Array.isArray(value)
}

function unwrap(message: string) {
  const text = message.replace(/^Error:\s*/, "").trim()

  const parse = (value: string) => {
    try {
      return JSON.parse(value) as unknown
    } catch {
      return undefined
    }
  }

  const read = (value: string) => {
    const first = parse(value)
    if (typeof first !== "string") return first
    return parse(first.trim())
  }

  let json = read(text)

  if (json === undefined) {
    const start = text.indexOf("{")
    const end = text.lastIndexOf("}")
    if (start !== -1 && end > start) {
      json = read(text.slice(start, end + 1))
    }
  }

  if (!record(json)) return message

  const err = record(json.error) ? json.error : undefined
  if (err) {
    const type = typeof err.type === "string" ? err.type : undefined
    const msg = typeof err.message === "string" ? err.message : undefined
    if (type && msg) return `${type}: ${msg}`
    if (msg) return msg
    if (type) return type
    const code = typeof err.code === "string" ? err.code : undefined
    if (code) return code
  }

  const msg = typeof json.message === "string" ? json.message : undefined
  if (msg) return msg

  const reason = typeof json.error === "string" ? json.error : undefined
  if (reason) return reason

  return message
}

function same<T>(a: readonly T[], b: readonly T[]) {
  if (a === b) return true
  if (a.length !== b.length) return false
  return a.every((x, i) => x === b[i])
}

function list<T>(value: T[] | undefined | null, fallback: T[]) {
  if (Array.isArray(value)) return value
  return fallback
}

const BROKEN_TOOL_HISTORY =
  /must be followed by tool messages responding to each|insufficient tool messages following tool_calls|No tool output found for tool call/i

// SQLite reporting that the chat database FILE is damaged (the sidecar's
// storage/db-health.ts). Same two sentences it recognises there.
const DATABASE_DAMAGED = /database disk image is malformed|file is not a database/i

/** The URL an SDK error was sent to, when it carries one. */
function metadataUrlOf(data: unknown): string | undefined {
  const url = (data as { metadata?: { url?: unknown } } | undefined)?.metadata?.url
  return typeof url === "string" ? url : undefined
}

/** The HTTP status an SDK error carries, when it carries one. */
function statusCodeOf(data: unknown): number | undefined {
  const status = (data as { statusCode?: unknown } | undefined)?.statusCode
  return typeof status === "number" ? status : undefined
}

export function SessionTurn(
  props: ParentProps<{
    sessionID: string
    messageID: string
    messages?: MessageType[]
    actions?: UserActions
    showReasoningSummaries?: boolean
    shellToolDefaultOpen?: boolean
    editToolDefaultOpen?: boolean
    active?: boolean
    status?: SessionStatus
    // The plan dock shows this turn's live activity (see ThinkingStatus).
    activityInDock?: boolean
    showActivity?: boolean
    onUserInteracted?: () => void
    classes?: {
      root?: string
      content?: string
      container?: string
    }
  }>,
) {
  const data = useData()
  const i18n = useI18n()

  const emptyMessages: MessageType[] = []
  const emptyParts: PartType[] = []
  const emptyAssistant: AssistantMessage[] = []
  const idle = { type: "idle" as const }

  const allMessages = createMemo(() => props.messages ?? list(data.store.message?.[props.sessionID], emptyMessages))

  const messageIndex = createMemo(() => {
    const messages = allMessages() ?? emptyMessages
    const result = Binary.search(messages, props.messageID, (m) => m.id)

    const index = result.found ? result.index : messages.findIndex((m) => m.id === props.messageID)
    if (index < 0) return -1

    const msg = messages[index]
    if (!msg || msg.role !== "user") return -1

    return index
  })

  const message = createMemo(() => {
    const index = messageIndex()
    if (index < 0) return undefined

    const messages = allMessages() ?? emptyMessages
    const msg = messages[index]
    if (!msg || msg.role !== "user") return undefined

    return msg
  })

  const pending = createMemo(() => {
    if (typeof props.active === "boolean") return
    const messages = allMessages() ?? emptyMessages
    return messages.findLast(
      (item): item is AssistantMessage => item.role === "assistant" && typeof item.time.completed !== "number",
    )
  })

  const pendingUser = createMemo(() => {
    const item = pending()
    if (!item?.parentID) return
    const messages = allMessages() ?? emptyMessages
    const result = Binary.search(messages, item.parentID, (m) => m.id)
    const msg = result.found ? messages[result.index] : messages.find((m) => m.id === item.parentID)
    if (!msg || msg.role !== "user") return
    return msg
  })

  const active = createMemo(() => {
    if (typeof props.active === "boolean") return props.active
    const msg = message()
    const parent = pendingUser()
    if (!msg || !parent) return false
    return parent.id === msg.id
  })

  const parts = createMemo(() => {
    const msg = message()
    if (!msg) return emptyParts
    return list(data.store.part?.[msg.id], emptyParts)
  })

  const compaction = createMemo(() => parts().find((part) => part.type === "compaction"))

  const assistantMessages = createMemo(
    () => {
      const msg = message()
      if (!msg) return emptyAssistant

      const messages = allMessages() ?? emptyMessages
      if (messageIndex() < 0) return emptyAssistant

      const result: AssistantMessage[] = []
      for (let i = 0; i < messages.length; i++) {
        const item = messages[i]
        if (!item) continue
        if (item.role === "assistant" && item.parentID === msg.id) result.push(item as AssistantMessage)
      }
      return result
    },
    emptyAssistant,
    { equals: same },
  )
  const showReasoningSummaries = createMemo(() => props.showReasoningSummaries ?? true)
  const assistantPartEntries = createMemo(() =>
    assistantMessages().flatMap((message) =>
      list(data.store.part?.[message.id], emptyParts).map((part) => ({
        messageID: message.id,
        part,
      })),
    ),
  )
  // Memoized: the status row reads it from three places, and while a call is
  // being written each read parses up to 2 MB of streamed arguments.
  const progress = createMemo(() => turnProgress(assistantPartEntries().map((entry) => entry.part)))
  const thinkingLayerPartIDs = createMemo(() => {
    const ids = new Set<string>()
    let hasLaterThinkingBoundary = false
    const entries = assistantPartEntries()
    const showReasoning = showReasoningSummaries()

    for (let i = entries.length - 1; i >= 0; i--) {
      const part = entries[i]!.part
      if (isThinkingLayerPart(part)) {
        // Keep the saved-memory chip out of the thinking layer so it renders as
        // persistent answer content, but still treat it as a boundary so any
        // narration text emitted before it stays collapsed.
        if (isMemoryChipPart(part)) {
          hasLaterThinkingBoundary = true
          continue
        }
        if (isAssistantPartRenderable(part, showReasoning)) ids.add(part.id)
        if (isThinkingTimelineBoundary(part, showReasoning)) hasLaterThinkingBoundary = true
        continue
      }
      if (part.type === "text" && part.text?.trim() && hasLaterThinkingBoundary) ids.add(part.id)
    }

    return ids
  })
  const isThinkingTimelinePart = (part: PartType) => thinkingLayerPartIDs().has(part.id)
  const isAssistantContentPart = (part: PartType) => !isThinkingTimelinePart(part)

  const interrupted = createMemo(() => assistantMessages().some((m) => m.error?.name === "MessageAbortedError"))
  const appClosed = createMemo(() => closedByApp(assistantMessages()))
  const completion = createMemo(() => assistantMessages().findLast((item) => item.completion)?.completion)
  const completionChecks = createMemo(() => {
    const evidence = completion()
    if (!evidence) return []
    const requirementCommands = new Set(evidence.requirements.map((requirement) => requirement.command.trim()))
    return currentChecks(evidence.checks).filter((check) => !requirementCommands.has(check.command.trim()))
  })
  const completionDetailCount = createMemo(() => completionChecks().length + (completion()?.requirements.length ?? 0))
  const completionHasDetails = createMemo(
    () => completionDetailCount() > 0 || (completion()?.review.status ?? "not_run") !== "not_run",
  )
  const completionLabel = createMemo(() => {
    const evidence = completion()
    if (!evidence) return ""
    return i18n.t(`ui.sessionTurn.verification.${evidence.verdict}`)
  })
  const [completionOpen, setCompletionOpen] = createSignal(false)
  const divider = createMemo(() => {
    if (compaction()) return i18n.t("ui.messagePart.compaction")
    if (appClosed()) return i18n.t("ui.message.interruptedAppClosed")
    if (interrupted()) return i18n.t("ui.message.interrupted")
    return ""
  })
  const failedMessage = createMemo(() =>
    assistantMessages().find((m) => m.error && m.error.name !== "MessageAbortedError"),
  )
  const error = createMemo(() => failedMessage()?.error)
  // The gateway's per-install day budget (DeepSeek $/day, probation caps),
  // recognised by its marker — see session-turn-daily-budget. Undefined for
  // every other error; the card text and the two extra actions key on it.
  const dailyBudget = createMemo(() => dailyBudgetError(error()?.data))
  // A context overflow that ends up ON a turn is terminal. The sidecar answers
  // an overflow by compacting and only stores one when even that could not fit
  // (session/compaction.ts) or the request after it was refused again
  // (session/prompt.ts). Retry would send the same oversized history — the
  // loop one install went round 18 times in an hour — so the card offers a
  // fresh chat instead.
  const tooLarge = createMemo(() => error()?.name === "ContextOverflowError")
  const dailyBudgetTime = createMemo(() => {
    const at = dailyBudget()?.resetAt
    return at === undefined ? "" : formatClock(at, i18n.locale())
  })
  const showAssistantCopyPartID = createMemo(() => {
    const messages = assistantMessages()

    for (let i = messages.length - 1; i >= 0; i--) {
      const message = messages[i]
      if (!message) continue

      const parts = list(data.store.part?.[message.id], emptyParts)
      for (let j = parts.length - 1; j >= 0; j--) {
        const part = parts[j]
        if (!part || part.type !== "text" || !part.text?.trim()) continue
        return part.id
      }
    }

    return undefined
  })
  const errorText = createMemo(() => {
    // The provider never came up — no request was sent, so neither the raw
    // name nor "the request failed" describes it (session-turn-provider-init).
    const init = providerInitCopy(error(), failedMessage()?.providerID)
    if (init) return i18n.t(init.key, init.params)
    // The raw text ("Session too large to compact - …") names a mechanism, not
    // what to do; the card's own button is the way out.
    if (tooLarge()) return i18n.t("ui.sessionTurn.error.sessionTooLarge")
    const data = error()?.data
    const msg = data && "message" in data ? data.message : undefined
    if (msg === undefined || msg === null) {
      const name = error()?.name
      return name ? i18n.t("ui.sessionTurn.error.fallback", { name }) : ""
    }
    // oxlint-disable-next-line no-base-to-string -- msg is unknown from error data, coercion is intentional
    const text = unwrap(typeof msg === "string" ? msg : String(msg))
    // The ChatGPT Codex backend rejects API-key-only and sunsetted models with
    // a bare Bad Request; say what the user can actually do about it.
    const codex = text.match(/The '([^']+)' model is not supported when using Codex with a ChatGPT account/)
    if (codex) return i18n.t("ui.sessionTurn.error.codexModelNotSupported", { model: codex[1] })
    // A tool call left without its result. Two providers, two wordings, one
    // cause: the turn died between the call and the answer, and the gap is now
    // stored — so the model rejects every later turn too, and the chat reads as
    // dead while a new one works. The raw text names `tool_call_id`, which tells
    // a user nothing; the repair that makes Retry work lives in
    // provider/tool-pairing-body.ts.
    if (BROKEN_TOOL_HISTORY.test(text)) return i18n.t("ui.sessionTurn.error.brokenToolHistory")
    // The chat database itself is damaged — the turn failed before any model
    // was asked, so "Retry" and switching models change nothing, and the raw
    // sentence reads like a failing disk. What does help is a restart: the
    // sidecar verifies the file when it opens it and restores the newest
    // snapshot. One user reported this as "neither GigaChat nor DeepSeek
    // starts" (feedback #217).
    if (DATABASE_DAMAGED.test(text)) return i18n.t("ui.sessionTurn.error.databaseDamaged")
    // An upstream refusing an API KEY, as opposed to refusing the request. The
    // sentence names four characters of a key the user very likely does not
    // remember setting: the desktop takes any DEEPSEEK_API_KEY it finds in the
    // environment as a BYOK opt-in, so a key set once for something else, or
    // long expired, silently replaces the built-in access. Say where it came
    // from and where the switch is — the raw text says neither, and the one
    // user this reached spent a day thinking the product had broken.
    const refusedSuffix = refusedKeySuffix(text)
    if (refusedSuffix) return i18n.t("ui.sessionTurn.error.upstreamKeyRefused", { suffix: refusedSuffix })
    // The upstream saying "no such model/endpoint here" — two words that a user
    // reads as a verdict on whichever model they think they are talking to.
    // One report blamed DeepSeek for exactly this while the 404s were coming
    // from providers they had added themselves. Name the provider and the
    // model, and say which kind of not-found it is: for a provider they
    // configured the fix is in their own settings, for one we broker there is
    // no setting for them to correct.
    // A 402 from OUR gateway is our account's balance, not the user's: the
    // upstream's "Insufficient Balance" was read as a bill to pay. Replaced
    // outright — here the provider's own words are the harm, not the clue.
    if (brokeredBalanceRefused({ status: statusCodeOf(error()?.data), url: metadataUrlOf(error()?.data) })) {
      return i18n.t("ui.sessionTurn.error.brokeredBalance", { provider: failedMessage()?.providerID ?? "" })
    }
    const notFound = modelNotFound({
      status: statusCodeOf(error()?.data),
      text,
      provider: failedMessage()?.providerID,
      model: failedMessage()?.modelID,
      url: metadataUrlOf(error()?.data),
    })
    if (notFound) {
      // The upstream's own words are KEPT, not replaced. Some of these 404s
      // carry the only concrete clue there is — Google answers that a video
      // model "is not supported for generateContent", OpenRouter that no
      // endpoint matches the account's data policy — and neither is "your
      // model id is wrong". The sentence adds who and what; the original says
      // why.
      const explained = i18n.t(
        notFound.brokered ? "ui.sessionTurn.error.modelNotFoundBrokered" : "ui.sessionTurn.error.modelNotFound",
        { provider: notFound.provider, model: notFound.model },
      )
      return /^\s*not found\.?\s*$/i.test(text) ? explained : `${explained}\n\n${text}`
    }
    // The gateway's per-install day budget (DeepSeek $/day, probation caps).
    // The sidecar makes it terminal instead of retrying until midnight; here
    // it gets a sentence and the reset time — the body carries an absolute
    // `reset_at` so the card is right even when read hours later.
    if (dailyBudget()) {
      const time = dailyBudgetTime()
      return time
        ? i18n.t("ui.sessionTurn.error.dailyBudgetExceeded", { time })
        : i18n.t("ui.sessionTurn.error.dailyBudgetExceededNoTime")
    }
    return text
  })

  const status = createMemo(() => {
    if (props.status !== undefined) return props.status
    if (typeof props.active === "boolean" && !props.active) return idle
    return data.store.session_status[props.sessionID] ?? idle
  })
  // The row's phase with the connection folded in (withConnectionPhase), and
  // the episode behind it: when the request left, which attempt this is, and
  // why the last one failed.
  const shownProgress = createMemo(() => withConnectionPhase(progress(), status()))
  const connection = createMemo(() => {
    const phase = shownProgress().phase
    if (phase !== "connecting" && phase !== "reconnecting") return undefined
    const current = status()
    if (current.type === "retry") return { attempt: current.attempt + 1, retried: true, reason: current.message }
    if (current.type === "busy" && current.connecting) {
      const reason = current.connecting.reason
      // "retried" means a NETWORK retry: only those carry a reason. After a
      // server refusal the hint about VPNs would point the wrong way.
      return { since: current.connecting.since, attempt: current.connecting.attempt, retried: !!reason, reason }
    }
    return undefined
  })
  // `status` is a one-shot event latch that can be lost (a reconnect
  // snapshot racing the busy transition, a dropped frame). When that happened
  // this row flipped to «Выполнено» mid-turn while the model kept working —
  // so the pending assistant message, which is durable, backs it up: an
  // active turn with an incomplete unerrored message stays "working". A
  // genuinely dead message is errored by the dead-turn watchdog within ten
  // minutes, which bounds the other direction.
  const working = createMemo(() => {
    if (!active()) return false
    if (status().type !== "idle") return true
    const message = pending()
    return message !== undefined && !message.error
  })

  const [retrying, setRetrying] = createSignal(false)

  // Retry re-runs this turn by REVERTING to it first, which discards every
  // message after it. That is only ever what the user means on the newest
  // turn — offering it on an old error card would silently delete all the
  // work done since. So: last turn in the session, and nothing running.
  const isLastTurn = createMemo(() => {
    const msg = message()
    if (!msg) return false
    const messages = allMessages() ?? emptyMessages
    for (let i = messages.length - 1; i >= 0; i--) {
      const item = messages[i]
      if (!item || item.role !== "user") continue
      return item.id === msg.id
    }
    return false
  })

  const retryable = createMemo(() => isLastTurn() && status().type === "idle")

  // The two daily-budget recoveries. «Switch to GigaChat» only makes sense
  // for a turn that ran on DeepSeek (the budget is DeepSeek's) and only when
  // the app wired the action — it has no GigaChat provider otherwise. «Send at
  // reset» needs the app's queue; either is hidden when its callback is absent.
  const switchable = createMemo(
    () => !!dailyBudget() && !!props.actions?.switchToGigaChat && failedMessage()?.providerID === "deepseek",
  )
  const schedulable = createMemo(() => !!dailyBudget() && !!props.actions?.sendAtReset)
  const freshable = createMemo(() => tooLarge() && !!props.actions?.startNewChat)
  const retriable = createMemo(() => !!props.actions?.retry && !tooLarge())

  // One in-flight guard for every button on the card: retry reverts the turn,
  // so a second action racing it would act on a session mid-revert.
  const runAction = (action: () => Promise<void> | void) => {
    if (retrying()) return
    setRetrying(true)
    void Promise.resolve(action()).finally(() => setRetrying(false))
  }

  const assistantCopyPartID = createMemo(() => {
    if (working()) return null
    return showAssistantCopyPartID() ?? null
  })
  const turnDurationMs = createMemo(() => {
    const start = message()?.time.created
    if (typeof start !== "number") return undefined

    const end = assistantMessages().reduce<number | undefined>((max, item) => {
      const completed = item.time.completed
      if (typeof completed !== "number") return max
      if (max === undefined) return completed
      return Math.max(max, completed)
    }, undefined)

    if (typeof end !== "number") return undefined
    if (end < start) return undefined
    return end - start
  })
  const assistantDerived = createMemo(() => {
    let answer = false
    let assistantContent = false
    let awaitingQuestion = false
    let toolActive = false
    const thinkingIDs = thinkingLayerPartIDs()
    for (const message of assistantMessages()) {
      for (const part of list(data.store.part?.[message.id], emptyParts)) {
        const inThinkingLayer = thinkingIDs.has(part.id)
        if (isAssistantPartRenderable(part, showReasoningSummaries()) && !inThinkingLayer) {
          assistantContent = true
        }
        if (part.type === "text" && part.text?.trim() && !inThinkingLayer) {
          answer = true
        }
        if (part.type === "tool" && part.tool === "question") {
          awaitingQuestion = part.state.status === "pending" || part.state.status === "running"
        }
        if (
          part.type === "tool" &&
          part.tool !== "question" &&
          (part.state.status === "pending" || part.state.status === "running")
        ) {
          toolActive = true
        }
      }
    }
    return { answer, assistantContent, awaitingQuestion, toolActive }
  })
  const showThinking = createMemo(() => {
    if (!working() || !!error()) return false
    // A network retry stays in this row as "reconnecting"; a server refusal
    // gets the error card (SessionRetry) instead.
    const current = status()
    if (current.type === "retry" && !current.network) return false
    if (assistantDerived().awaitingQuestion) return false
    // The row lives exactly as long as the turn. It used to unmount the
    // moment the first text part landed with no tool running — the model's
    // «сейчас исправлю…» preamble then sat on screen for tens of seconds
    // with nothing saying work continues (the field's «куда пропала шкала
    // прогресса?»), until the next tool part brought the row back.
    return true
  })
  const hasThinkingLayer = createMemo(() =>
    assistantPartEntries().some(
      (entry) =>
        thinkingLayerPartIDs().has(entry.part.id) && isAssistantPartRenderable(entry.part, showReasoningSummaries()),
    ),
  )
  const showThinkingDropdown = createMemo(() => showThinking() || hasThinkingLayer())
  const answerReady = createMemo(() => !working() && assistantDerived().assistantContent)
  const [thinkingOpen, setThinkingOpen] = createSignal(false)
  const [thinkingTouched, setThinkingTouched] = createSignal(false)
  createEffect(
    on(hasThinkingLayer, (available) => {
      if (!available) {
        setThinkingOpen(false)
        setThinkingTouched(false)
        return
      }
      if (!thinkingTouched()) setThinkingOpen(true)
    }),
  )
  createEffect(
    on(answerReady, (ready) => {
      if (ready) setThinkingOpen(false)
    }),
  )

  const autoScroll = createAutoScroll({
    working,
    onUserInteracted: props.onUserInteracted,
    overflowAnchor: "dynamic",
  })

  return (
    <div data-component="session-turn" class={props.classes?.root}>
      <div
        ref={autoScroll.scrollRef}
        onScroll={autoScroll.handleScroll}
        data-slot="session-turn-content"
        class={props.classes?.content}
      >
        <div onClick={autoScroll.handleInteraction}>
          <Show when={message()}>
            <div
              ref={autoScroll.contentRef}
              data-message={message()!.id}
              data-slot="session-turn-message-container"
              class={props.classes?.container}
            >
              <div data-slot="session-turn-message-content" aria-live="off">
                <Message message={message()!} parts={parts()} actions={props.actions} />
              </div>
              <Show when={divider()}>
                <div data-slot="session-turn-compaction">
                  <MessageDivider label={divider()} />
                </div>
              </Show>
              <Show when={showThinkingDropdown()}>
                <div
                  data-slot="session-turn-thinking"
                  data-expanded={thinkingOpen() ? "" : undefined}
                  data-has-layer={hasThinkingLayer() ? "" : undefined}
                >
                  <button
                    type="button"
                    data-slot="session-turn-thinking-trigger"
                    aria-expanded={hasThinkingLayer() ? thinkingOpen() : undefined}
                    disabled={!hasThinkingLayer()}
                    onClick={() => {
                      if (!hasThinkingLayer()) return
                      setThinkingTouched(true)
                      setThinkingOpen((open) => !open)
                    }}
                  >
                    <ThinkingStatus
                      working={working()}
                      createdAt={message()?.time.created}
                      durationMs={turnDurationMs()}
                      progress={shownProgress()}
                      connectionAttempt={connection()?.attempt}
                      activityInDock={props.activityInDock}
                      showActivity={props.showActivity}
                    />
                    <Show when={hasThinkingLayer()}>
                      <Icon name="chevron-right" size="normal" class="session-turn-thinking-chevron" />
                    </Show>
                  </button>
                  <Show when={working() && connection()}>
                    {(episode) => (
                      <ConnectionHint since={episode().since} retried={episode().retried} reason={episode().reason} />
                    )}
                  </Show>
                  <Show when={hasThinkingLayer() && thinkingOpen()}>
                    <div data-slot="session-turn-thinking-body">
                      <AssistantParts
                        messages={assistantMessages()}
                        turnDurationMs={turnDurationMs()}
                        working={working()}
                        showReasoningSummaries={showReasoningSummaries()}
                        shellToolDefaultOpen={props.shellToolDefaultOpen}
                        editToolDefaultOpen={props.editToolDefaultOpen}
                        includePart={isThinkingTimelinePart}
                        showAssistantCopyPartID={null}
                        suppressArtifactPreviews={true}
                        showAllActivity={true}
                      />
                    </div>
                  </Show>
                </div>
              </Show>
              <Show when={assistantMessages().length > 0 && assistantDerived().assistantContent}>
                <div data-slot="session-turn-assistant-content" aria-hidden={working()}>
                  <AssistantParts
                    messages={assistantMessages()}
                    showAssistantCopyPartID={assistantCopyPartID()}
                    turnDurationMs={turnDurationMs()}
                    working={working()}
                    showReasoningSummaries={showReasoningSummaries()}
                    shellToolDefaultOpen={props.shellToolDefaultOpen}
                    editToolDefaultOpen={props.editToolDefaultOpen}
                    includePart={isAssistantContentPart}
                  />
                </div>
              </Show>
              <Show
                when={
                  !working() &&
                  !error() &&
                  !interrupted() &&
                  assistantMessages().length > 0 &&
                  finished(assistantMessages()) &&
                  !assistantDerived().assistantContent &&
                  !assistantDerived().awaitingQuestion &&
                  !assistantDerived().toolActive
                }
              >
                <p data-slot="session-turn-empty-answer" class="text-13-regular text-text-weak">
                  {i18n.t("ui.sessionTurn.emptyAnswer")}
                </p>
              </Show>
              <Show
                when={
                  !working() &&
                  completion()?.policy !== "observe" &&
                  completion()?.verdict !== "not_required" &&
                  completion()?.verdict !== "unverified" &&
                  completion()
                }
              >
                {(evidence) => (
                  <div data-slot="session-turn-verification" data-verdict={evidence().verdict} class="mt-2">
                    <button
                      type="button"
                      class="flex items-center gap-1 text-12-regular text-text-weak disabled:cursor-default"
                      disabled={!completionHasDetails()}
                      aria-expanded={completionHasDetails() ? completionOpen() : undefined}
                      aria-label={
                        !completionHasDetails()
                          ? completionLabel()
                          : `${completionLabel()}. ${i18n.t(
                              completionOpen()
                                ? "ui.sessionTurn.verification.details.hide"
                                : "ui.sessionTurn.verification.details.show",
                            )}`
                      }
                      onClick={() => setCompletionOpen((open) => !open)}
                    >
                      <Icon
                        name={
                          evidence().verdict === "passed"
                            ? "circle-check"
                            : evidence().verdict === "failed"
                              ? "circle-x"
                              : "help"
                        }
                        size="small"
                        classList={{
                          "text-icon-success-base": evidence().verdict === "passed",
                          "text-icon-critical-base": evidence().verdict === "failed",
                          "text-icon-weak": evidence().verdict === "unverified",
                        }}
                      />
                      <span>{completionLabel()}</span>
                      <Show when={completionHasDetails()}>
                        <span>
                          ·{" "}
                          {i18n.t(
                            completionDetailCount() === 1
                              ? "ui.sessionTurn.verification.checks.one"
                              : "ui.sessionTurn.verification.checks.other",
                            { count: completionDetailCount() },
                          )}
                        </span>
                        <Icon name="chevron-right" size="small" classList={{ "rotate-90": completionOpen() }} />
                      </Show>
                    </button>
                    <Show when={(evidence().attempts ?? 0) > 0}>
                      <div class="mt-1 text-11-regular text-text-weaker">
                        {i18n.t(
                          (evidence().attempts ?? 0) === 1
                            ? "ui.sessionTurn.verification.attempts.one"
                            : "ui.sessionTurn.verification.attempts.other",
                          { count: evidence().attempts ?? 0 },
                        )}
                      </div>
                    </Show>
                    <Show when={completionHasDetails() && completionOpen()}>
                      <div class="mt-2 flex flex-col gap-2 rounded-md border border-border-weak-base p-2">
                        <Show when={evidence().review.status !== "not_run"}>
                          <div data-slot="session-turn-verification-review" class="min-w-0">
                            <div class="text-12-medium text-text-base">
                              {i18n.t(`ui.sessionTurn.verification.review.${evidence().review.status}`)}
                            </div>
                            <For each={evidence().review.findings}>
                              {(finding) => (
                                <div class="mt-1 border-l-2 border-border-weak-base pl-2 text-11-regular text-text-weak">
                                  <span class="font-medium">{finding.severity}</span> · {finding.message}
                                  <Show when={finding.evidence}>
                                    <div class="mt-0.5 text-text-weaker">{finding.evidence}</div>
                                  </Show>
                                </div>
                              )}
                            </For>
                          </div>
                        </Show>
                        <For each={evidence().requirements}>
                          {(requirement) => (
                            <div data-slot="session-turn-verification-requirement" class="min-w-0">
                              <div class="flex items-center gap-1 text-12-medium text-text-base">
                                <Icon
                                  name={
                                    requirement.status === "passed"
                                      ? "circle-check"
                                      : requirement.status === "failed"
                                        ? "circle-x"
                                        : "help"
                                  }
                                  size="small"
                                  classList={{
                                    "text-icon-success-base": requirement.status === "passed",
                                    "text-icon-critical-base": requirement.status === "failed",
                                    "text-icon-warning-base": requirement.status === "missing" && requirement.required,
                                    "text-icon-weak": requirement.status === "missing" && !requirement.required,
                                  }}
                                />
                                <span>{requirement.name}</span>
                                <Show when={requirement.status === "missing"}>
                                  <span class="text-11-regular text-text-weaker">
                                    ·
                                    {i18n.t(
                                      requirement.required
                                        ? "ui.sessionTurn.verification.requirement.missing"
                                        : "ui.sessionTurn.verification.requirement.optional",
                                    )}
                                  </span>
                                </Show>
                              </div>
                              <code class="mt-1 block truncate text-11-mono text-text-weaker">
                                {requirement.command}
                              </code>
                            </div>
                          )}
                        </For>
                        <For each={completionChecks()}>
                          {(check) => (
                            <div data-slot="session-turn-verification-check" class="min-w-0">
                              <div class="flex items-center gap-1 text-12-medium text-text-base">
                                <Icon
                                  name={check.status === "passed" ? "circle-check" : "circle-x"}
                                  size="small"
                                  class={
                                    check.status === "passed" ? "text-icon-success-base" : "text-icon-critical-base"
                                  }
                                />
                                <code class="truncate">{check.command}</code>
                              </div>
                              <div class="mt-1 text-11-regular text-text-weaker">
                                {i18n.t("ui.sessionTurn.verification.exitCode", { code: check.exitCode })}
                              </div>
                              <Show when={check.output.trim()}>
                                <pre class="mt-1 max-h-40 overflow-auto whitespace-pre-wrap break-words rounded bg-background-weak p-2 text-11-mono text-text-weak">
                                  {check.output}
                                </pre>
                              </Show>
                            </div>
                          )}
                        </For>
                      </div>
                    </Show>
                  </div>
                )}
              </Show>
              <SessionRetry status={status()} show={active() && !connection()?.retried} />
              <Show when={appClosed() && retryable() && props.actions?.retry}>
                <div data-slot="session-turn-error-actions">
                  <Button
                    size="sm"
                    variant="outline"
                    icon="arrow-undo-down"
                    disabled={retrying()}
                    onClick={() =>
                      runAction(() => props.actions?.retry?.({ sessionID: props.sessionID, messageID: props.messageID }))
                    }
                  >
                    {i18n.t("ui.sessionTurn.error.retry")}
                  </Button>
                </div>
              </Show>
              <Show when={error()}>
                <Card variant="error" class="error-card">
                  {errorText()}
                </Card>
                <Show when={retryable() && (retriable() || switchable() || schedulable() || freshable())}>
                  <div data-slot="session-turn-error-actions">
                    <Show when={freshable()}>
                      <Button
                        size="sm"
                        variant="primary"
                        icon="new-session"
                        disabled={retrying()}
                        onClick={() =>
                          runAction(() =>
                            props.actions?.startNewChat?.({ sessionID: props.sessionID, messageID: props.messageID }),
                          )
                        }
                      >
                        {i18n.t("ui.sessionTurn.error.startNewChat")}
                      </Button>
                    </Show>
                    <Show when={retriable()}>
                      <Button
                        size="sm"
                        variant="outline"
                        icon="arrow-undo-down"
                        disabled={retrying()}
                        onClick={() =>
                          runAction(() =>
                            props.actions?.retry?.({ sessionID: props.sessionID, messageID: props.messageID }),
                          )
                        }
                      >
                        {i18n.t("ui.sessionTurn.error.retry")}
                      </Button>
                    </Show>
                    <Show when={switchable()}>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={retrying()}
                        onClick={() =>
                          runAction(() =>
                            props.actions?.switchToGigaChat?.({
                              sessionID: props.sessionID,
                              messageID: props.messageID,
                            }),
                          )
                        }
                      >
                        {i18n.t("ui.sessionTurn.error.switchToGigaChat")}
                      </Button>
                    </Show>
                    <Show when={schedulable()}>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={retrying()}
                        onClick={() =>
                          runAction(() =>
                            props.actions?.sendAtReset?.({
                              sessionID: props.sessionID,
                              messageID: props.messageID,
                              resetAt: dailyBudget()?.resetAt,
                            }),
                          )
                        }
                      >
                        {dailyBudgetTime()
                          ? i18n.t("ui.sessionTurn.error.sendAtReset", { time: dailyBudgetTime() })
                          : i18n.t("ui.sessionTurn.error.sendAtResetNoTime")}
                      </Button>
                    </Show>
                  </div>
                </Show>
              </Show>
            </div>
          </Show>
          {props.children}
        </div>
      </div>
    </div>
  )
}
